/**
 * OwnersStep Component
 *
 * Fourth step: Add additional owners (25%+ ownership stake).
 * Only required for non-sole proprietor businesses (LLC, Corp, Partnership, LLP).
 * User can add multiple owners or skip if no additional owners.
 */

import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import type { IMerchant, IOnboardingApplication } from '@order-up/models';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { useSaveDraft } from '../../lib/hooks';
import { US_STATES } from '../../types/onboarding';

export interface OwnersStepProps {
  application: IOnboardingApplication;
}

// Onboarding additional owner structure (comprehensive for our needs)
// Mapped onto the merchant application's additional-owner shape on submission
export interface AdditionalOwner {
  ownerName: string;
  ownerEmail: string;
  ownerDob: string;
  ownershipPercentage: string;
  ownerAddress: {
    address1: string;
    city: string;
    stateCd: string;
    zip: string;
  };
}

export function OwnersStep({ application }: OwnersStepProps) {
  const { mutate: saveDraft, isPending } = useSaveDraft();

  const appData = application.applicationData;
  const isSoleProp = appData.ownership?.ownershipTypeCd === 'INDIVSOLE';

  // Initialize with existing additional owners or empty array
  const [additionalOwners, setAdditionalOwners] = useState<AdditionalOwner[]>(
    (appData.ownership?.additionalOwners as AdditionalOwner[]) || []
  );

  // Add new owner
  const addOwner = () => {
    const newOwner: AdditionalOwner = {
      ownerName: '',
      ownerEmail: '',
      ownerDob: '',
      ownershipPercentage: '',
      ownerAddress: {
        address1: '',
        city: '',
        stateCd: '',
        zip: ''
      }
    };
    const updated = [...additionalOwners, newOwner];
    setAdditionalOwners(updated);
    saveOwners(updated);
  };

  // Remove owner
  const removeOwner = (index: number) => {
    const updated = additionalOwners.filter((_, i) => i !== index);
    setAdditionalOwners(updated);
    saveOwners(updated);
  };

  const updateOwner = (index: number, field: string, rawValue: string) => {
    const updated = [...additionalOwners];

    if (field.startsWith('ownerAddress.')) {
      const addressField = field.split('.')[1];
      updated[index] = {
        ...updated[index],
        ownerAddress: {
          ...updated[index].ownerAddress,
          [addressField]: rawValue,
        },
      };
    } else {
      updated[index] = {
        ...updated[index],
        [field]: rawValue,
      };
    }

    setAdditionalOwners(updated);
    saveOwners(updated);
  };

  // Save to database
  const saveOwners = (owners: AdditionalOwner[]) => {
    const updates: Partial<IMerchant> = {
      ownership: {
        ...appData.ownership,
        additionalOwners: owners
      }
    };

    saveDraft({
      id: application.id,
      data: updates,
      step: application.currentStep
    });
  };

  // Skip if sole proprietor
  if (isSoleProp) {
    return (
      <div className="space-y-6">
        <div className="rounded-lg border border-border/50 bg-zinc-900/30 p-6 text-center">
          <p className="text-zinc-300">
            As a Sole Proprietor, you don't need to provide information about additional owners.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <h3 className="text-lg font-medium text-white">Additional Owners (25%+ stake)</h3>
        <p className="text-sm text-zinc-400">
          Your information was already collected as the primary owner. Add any additional individuals
          who own 25% or more of the business below.
        </p>
      </div>

      {/* No owners message */}
      {additionalOwners.length === 0 && (
        <div className="rounded-lg border border-dashed border-zinc-700 bg-zinc-900/30 p-8 text-center">
          <p className="text-sm text-zinc-400 mb-4">No additional owners added yet</p>
          <button
            type="button"
            onClick={addOwner}
            disabled={isPending}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            <Plus className="h-4 w-4" />
            Add Owner
          </button>
        </div>
      )}

      {/* Owners list */}
      {additionalOwners.map((owner, index) => (
        <div
          key={index}
          className="rounded-xl border border-border bg-zinc-900/50 p-6 space-y-4"
        >
          {/* Header with remove button */}
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-md font-medium text-white">Owner {index + 2}</h4>
            <button
              type="button"
              onClick={() => removeOwner(index)}
              disabled={isPending}
              className="flex items-center gap-2 text-sm text-red-400 hover:text-red-300 transition-colors disabled:opacity-50"
            >
              <Trash2 className="h-4 w-4" />
              Remove
            </button>
          </div>

          {/* Owner fields */}
          <Input
            label="Full Legal Name"
            type="text"
            value={owner.ownerName}
            onChange={(e) => updateOwner(index, 'ownerName', e.target.value)}
            placeholder="Jane Smith"
            required
          />

          <Input
            label="Email Address"
            type="email"
            value={owner.ownerEmail}
            onChange={(e) => updateOwner(index, 'ownerEmail', e.target.value)}
            placeholder="jane@example.com"
            required
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="Date of Birth"
              type="date"
              value={owner.ownerDob}
              onChange={(e) => updateOwner(index, 'ownerDob', e.target.value)}
              required
            />

            <Input
              label="Ownership %"
              type="number"
              value={owner.ownershipPercentage}
              onChange={(e) => updateOwner(index, 'ownershipPercentage', e.target.value)}
              placeholder="25"
              min="25"
              max="100"
              required
            />
          </div>

          {/* Address */}
          <div className="space-y-4 pt-4 border-t border-border/50">
            <h5 className="text-sm font-medium text-zinc-300">Address</h5>

            <Input
              label="Address"
              type="text"
              value={owner.ownerAddress.address1}
              onChange={(e) => updateOwner(index, 'ownerAddress.address1', e.target.value)}
              placeholder="123 Main St"
              required
            />

            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                label="City"
                type="text"
                value={owner.ownerAddress.city}
                onChange={(e) => updateOwner(index, 'ownerAddress.city', e.target.value)}
                placeholder="San Francisco"
                required
              />

              <Select
                label="State"
                value={owner.ownerAddress.stateCd}
                onChange={(e) => updateOwner(index, 'ownerAddress.stateCd', e.target.value)}
                required
              >
                <option value="">Select State</option>
                {US_STATES.map((state) => (
                  <option key={state.value} value={state.value}>
                    {state.label}
                  </option>
                ))}
              </Select>
            </div>

            <Input
              label="ZIP Code"
              type="text"
              value={owner.ownerAddress.zip}
              onChange={(e) => updateOwner(index, 'ownerAddress.zip', e.target.value)}
              placeholder="94102"
              required
              maxLength={10}
            />
          </div>
        </div>
      ))}

      {/* Add another owner button */}
      {additionalOwners.length > 0 && (
        <button
          type="button"
          onClick={addOwner}
          disabled={isPending}
          className="w-full rounded-lg border border-dashed border-zinc-700 bg-zinc-900/30 px-4 py-3 text-sm font-medium text-zinc-300 hover:border-zinc-600 hover:bg-zinc-800/50 hover:text-white transition-all disabled:opacity-50"
        >
          <Plus className="h-4 w-4 inline mr-2" />
          Add Another Owner
        </button>
      )}

      {/* Info callout */}
      <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-4">
        <p className="text-sm text-amber-200/80">
          <span className="font-medium text-amber-300">Important:</span>
          {' '}You must disclose all individuals who own 25% or more of the business. This is
          required by law for anti-money laundering compliance.
        </p>
      </div>
    </div>
  );
}
